import { DAY, DEFAULT_CONFIG, MINUTE, TRUST_EVENTS } from './constants';
import { environment, identifier, journeyKey, reference, selection } from './helpers';
import { createJourney } from './journey';
import { evaluateRules } from './rules';
import { classify, getActivity, summarizeHistory } from './signals';
import { cleanRecords, createStorage, mergeRecord } from './storage';

const createTrust = ({
  scope = 'default',
  config = {},
  storage,
  now = Date.now,
  clock = () => performance.now(),
  publish = () => {},
} = {}) => {
  const options = {
    contactSignals: config.contactSignals === true,
    trustedScore: Number.isFinite(config.trustedScore)
      ? Math.min(100, Math.max(1, config.trustedScore))
      : DEFAULT_CONFIG.trustedScore,
  };
  const persistence = storage || createStorage(scope);
  const journey = createJourney(scope, now, clock);
  let records = persistence.read(now());
  let attempt;
  let assessment;
  let previousLevel;
  let stopped = true;
  let observedAt;
  let firstActionAt;
  let lastActionAt;
  let lastActivationAt;
  let activationTime;
  let loginObserved = false;
  let visibilityChanges = 0;
  let hidden;
  let qualifiedVisit;
  const fields = new Set();
  const navigation = { search: false, rates: false, selection: false, checkout: false };
  const copy = (value) => JSON.parse(JSON.stringify(value));
  let activeTime = 0;
  let activeSince;
  let contact;
  let contactRevision = 0;
  let contactKey;
  let lastEditAt;
  let lastEditTime;
  const paymentFlow = { events: [], counts: {}, source: 'componentCallbacks' };
  const validation = { currentErrors: 0, resolvedErrors: 0, failedSubmissions: 0 };
  const interaction = {
    inputs: 0,
    trustedActions: 0,
    activations: 0,
    focus: 0,
    pointerMoves: 0,
    scrolls: 0,
    events: {},
    edits: 0,
    pastes: 0,
    composition: 0,
    synthetic: 0,
    corrections: 0,
    navigationKeys: 0,
    finderChanges: 0,
    sources: {},
    modalities: [],
    timing: { samples: 0, mean: 0, variance: 0 },
  };

  const emptyInteraction = copy(interaction);
  let interactionContext;
  const setInteractionContext = (target) => {
    if (!target.start || !target.end || target.start >= target.end) return;
    const key = journeyKey(target);
    if (interactionContext && interactionContext !== key) {
      Object.assign(interaction, copy(emptyInteraction));
      fields.clear();
      firstActionAt = undefined;
      lastActionAt = undefined;
      lastActivationAt = undefined;
      activationTime = undefined;
      lastEditAt = undefined;
      lastEditTime = undefined;
    }
    interactionContext = key;
  };

  const emit = (event, response) => {
    try {
      publish(event, { event, response: copy(response) });
    } catch {
      return false;
    }
    return true;
  };

  const refresh = () => {
    const merged = new Map(persistence.read(now()).map((record) => [record.id, record]));
    records.forEach((record) => {
      merged.set(record.id, mergeRecord(merged.get(record.id), record));
    });
    records = cleanRecords([...merged.values()], now());
  };

  const persist = () => {
    records = persistence.write(records, now());
  };

  const update = (record) => {
    const index = records.findIndex(({ id }) => id === record.id);
    if (index >= 0) records[index] = { ...records[index], ...record };
    else records.push(record);
    records = cleanRecords(records, now());
    persist();
  };

  const assess = (context = {}, checkpoint = 'navigation') => {
    refresh();
    const currentTime = now();
    const target = selection(context);
    if (!interactionContext) setInteractionContext(target);
    const interactionMatches = interactionContext === journeyKey(target);
    const env = environment();
    if (
      interactionMatches &&
      fields.size >= 2 &&
      lastEditTime !== undefined &&
      lastActionAt - firstActionAt >= 1000 &&
      env.automated !== true &&
      env.headlessDeclared !== true
    )
      journey.rememberForm({
        key: journeyKey(target),
        at: lastEditTime,
        fields: fields.size,
        edits: interaction.edits + interaction.pastes + interaction.composition,
        spanMs: Math.min(DAY, lastActionAt - firstActionAt),
      });
    const { evidence: risks } = evaluateRules({ records, target, now: currentTime, automated: false, contact });
    const activeMs = Math.min(DAY, activeTime + (activeSince === undefined ? 0 : Math.max(0, clock() - activeSince)));
    const recentJourney = records.filter((record) => record.at <= currentTime && record.at > currentTime - 30 * MINUTE);
    const latest = (kind) =>
      recentJourney
        .filter(
          (record) =>
            record.kind === kind &&
            record.start === target.start &&
            record.end === target.end &&
            record.rooms === target.rooms,
        )
        .at(-1)?.at;
    const searchAt = latest('search');
    const selectionAt = latest('selection');
    const checkoutAt = latest('checkout');
    const ratesAt = latest('rates');
    const elapsed = (from, to) => (from !== undefined && to >= from ? to - from : null);
    const signals = {
      journey: journey.snapshot(),
      paymentFlow: copy(paymentFlow),
      validation: { ...validation, source: 'personalFormValidation' },
      environment: { ...env, source: 'browserDeclarations', observedAt: currentTime },
      observation: {
        available: observedAt !== undefined,
        activeMs: Math.round(activeMs),
        visibilityChanges,
        hidden: hidden ?? null,
        source: 'documentLifecycle',
      },
      interaction: {
        ...copy(interaction),
        contextMatches: interactionMatches,
        distinctFields: fields.size,
        lastActionAgeMs: lastActionAt === undefined ? null : Math.round(Math.max(0, clock() - lastActionAt)),
        lastEditAt: lastEditTime ?? null,
        lastActivationAt: activationTime ?? null,
        lastActivationAgeMs:
          lastActivationAt === undefined ? null : Math.round(Math.max(0, clock() - lastActivationAt)),
        spanMs: firstActionAt === undefined ? 0 : Math.round(Math.max(0, lastActionAt - firstActionAt)),
        source: 'scopedDOMEvents',
      },
      authentication: {
        sessionPresent: !!(context.session?.authorization || context.session?.accessToken),
        loginObserved,
        source: 'coreSession',
        serverVerified: false,
      },
      navigation: {
        ...navigation,
        searchAt: searchAt ?? null,
        checkoutAt: checkoutAt ?? null,
        rates: ratesAt !== undefined,
        searchToRatesMs: elapsed(searchAt, ratesAt),
        ratesToSelectionMs: elapsed(ratesAt, selectionAt),
        selectionToCheckoutMs: elapsed(selectionAt, checkoutAt),
        search: searchAt !== undefined,
        selection: selectionAt !== undefined,
        checkout: checkoutAt !== undefined,
        orderedJourney: searchAt !== undefined && selectionAt >= searchAt && checkoutAt >= selectionAt,
        source: 'coreBusinessActions',
      },
      history: { ...summarizeHistory(records, currentTime, target), available: persistence.available() },
      contact: { enabled: options.contactSignals, available: !!contact, source: 'localHMAC' },
      context: {
        journeyKey: journeyKey(target),
        browsingKey: journeyKey({ ...target, rooms: 0 }),
        evaluatedAt: currentTime,
        validStay: !!target.start && !!target.end && target.start < target.end,
        rooms: target.rooms,
        source: 'checkoutContext',
      },
    };
    const classification = classify(signals, risks, options.trustedScore);
    const activity = getActivity(signals);
    const coverage =
      observedAt === undefined || !signals.context.validStay
        ? 'limited'
        : activity.qualified && activeMs >= 1000
        ? 'sufficient'
        : 'partial';
    assessment = {
      ...classification,
      assessmentId: identifier(),
      attemptId: attempt?.id,
      checkpoint,
      coverage,
      threshold: options.trustedScore,
      signals,
      evaluatedAt: currentTime,
      version: 2,
      rulesVersion: 6,
    };
    const visitId = `visit:${Math.floor(currentTime / DAY)}`;
    if (
      activity.qualified &&
      activity.sustained &&
      qualifiedVisit !== visitId &&
      env.automated !== true &&
      env.headlessDeclared !== true
    ) {
      qualifiedVisit = visitId;
      update({ id: visitId, at: currentTime, kind: 'visit', qualified: true });
    }
    const level = `${assessment.trusted}:${coverage}:${assessment.reasons.join(',')}`;
    if (level !== previousLevel) {
      previousLevel = level;
      emit(TRUST_EVENTS.CHANGED, assessment);
    }
    return copy(assessment);
  };

  const evaluate = (context = {}, checkpoint = 'navigation') => {
    try {
      return assess(context, checkpoint);
    } catch {
      assessment = {
        trusted: false,
        score: 0,
        reasons: ['assessmentUnavailable'],
        evidence: [],
        coverage: 'limited',
        signals: {},
        assessmentId: identifier(),
        attemptId: attempt?.id,
        checkpoint,
        evaluatedAt: now(),
        threshold: options.trustedScore,
        version: 2,
      };
      return copy(assessment);
    }
  };

  const begin = (context) => {
    const target = selection(context);
    const method = String(context.payment?.method || '');
    attempt = { id: identifier(), at: now(), target, state: 'pending' };
    update({
      id: attempt.id,
      kind: 'attempt',
      state: 'pending',
      at: attempt.at,
      ...attempt.target,
      contact,
      payLater: method === 'PAYLATER',
      occupation: Array.isArray(context.finder?.occupation)
        ? reference(
            JSON.stringify(
              context.finder.occupation
                .slice(0, 100)
                .map(({ adults, children = [] }) => [
                  Number(adults) || 0,
                  Array.isArray(children) ? children.length : 0,
                ]),
            ),
          )
        : undefined,
    });
    return attempt;
  };

  const check = (context = {}, checkpoint = 'beforeConfirm') => {
    try {
      setInteractionContext(selection(context));
      if (
        checkpoint === 'beforePayment' ||
        !attempt ||
        attempt.state !== 'pending' ||
        now() - attempt.at >= 30 * MINUTE
      )
        begin(context);
    } catch {
      attempt = undefined;
    }
    const result = evaluate(context, checkpoint);
    emit(TRUST_EVENTS.EVALUATED, result);
    return result;
  };

  const observeResult = (context, response, error) => {
    try {
      if (!attempt) return;
      const current = attempt;
      const target = selection(context);
      if (error || !response || response.unconfirmed || (response.reservationCode && response.code))
        current.state = 'returned';
      update({
        id: current.id,
        kind: 'attempt',
        at: current.at,
        ...target,
        contact,
        state: error?.code === 6 ? 'failed' : 'returned',
      });
      if (response?.reservationCode && response.code && !response.unconfirmed) {
        const id = `booking:${reference(response.reservationCode)}`;
        const previous = records.find((item) => item.id === id);
        update({
          id,
          kind: 'booking',
          at: now(),
          createdAt: previous?.createdAt || now(),
          state: 'confirmed',
          ...target,
        });
      }
    } catch {
      return;
    }
  };

  const request = async (context, callback) => {
    const result = check(context, 'beforeConfirm');
    try {
      const response = await callback(copy(result));
      observeResult(context, response);
      return { response, assessment: result };
    } catch (error) {
      observeResult(context, undefined, error);
      throw error;
    }
  };

  const record = (action, context = {}) => {
    if (action === 'paymentFlow') {
      const { phase } = context;
      if (
        ![
          'pressAccepted',
          'validationRejected',
          'providerDelegated',
          'requestFromButton',
          'modalOpened',
          'modalClosed',
          'redirectStarted',
          'providerCallback',
          'paymentStateResume',
          'confirmationRequested',
          'errorCallback',
        ].includes(phase)
      )
        return;
      paymentFlow.counts[phase] = Math.min(10000, (paymentFlow.counts[phase] || 0) + 1);
      paymentFlow.events.push({ phase, at: now() });
      paymentFlow.events = paymentFlow.events.slice(-20);
      return;
    }
    if (action === 'validation') {
      if (Number.isInteger(context.count) && context.count >= 0)
        validation.currentErrors = Math.min(100, context.count);
      if (Number.isInteger(context.resolved) && context.resolved > 0)
        validation.resolvedErrors = Math.min(10000, validation.resolvedErrors + context.resolved);
      return;
    }
    if (action === 'validationFailed') {
      validation.failedSubmissions = Math.min(10000, validation.failedSubmissions + 1);
      return;
    }
    if (action === 'journeyAction') return journey.action(context);
    if (action === 'stageEntered') return journey.enter(context.stage);
    if (action === 'loginSucceeded') {
      loginObserved = true;
      return;
    }
    if (action === 'finderChanged') {
      interaction.finderChanges = Math.min(10000, interaction.finderChanges + 1);
      return;
    }
    if (action === 'formInitialized') {
      if (!['cache', 'schema', 'session'].includes(context.source)) return;
      const count = Math.min(100, Math.max(0, Number(context.count) || 0));
      interaction.sources[context.source] = count;
      return;
    }
    if (action === 'interaction') {
      if (
        !['inputs', 'edits', 'pastes', 'composition', 'synthetic', 'focus', 'pointerMoves', 'scrolls'].includes(
          context.type,
        )
      )
        return;
      interaction[context.type] = Math.min(10000, interaction[context.type] + 1);
      const meaningful = !['synthetic', 'focus', 'pointerMoves', 'scrolls'].includes(context.type);
      if (meaningful) {
        interaction.trustedActions = Math.min(10000, interaction.trustedActions + 1);
        if (firstActionAt === undefined) firstActionAt = clock();
        lastActionAt = clock();
        if (context.activation) {
          interaction.activations = Math.min(10000, interaction.activations + 1);
          lastActivationAt = clock();
          activationTime = now();
        }
        if (Number.isInteger(context.field) && context.field >= 0 && context.field < 100) fields.add(context.field);
      }
      if (
        [
          'input',
          'paste',
          'compositionend',
          'focusin',
          'pointerdown',
          'keydown',
          'click',
          'pointermove',
          'scroll',
          'change',
        ].includes(context.event)
      ) {
        interaction.events[context.event] = Math.min(10000, (interaction.events[context.event] || 0) + 1);
      }
      if (meaningful && ['mouse', 'touch', 'pen', 'keyboard'].includes(context.modality)) {
        interaction.modalities = [...new Set([...interaction.modalities, context.modality])];
      }
      if (meaningful && context.correction) interaction.corrections = Math.min(10000, interaction.corrections + 1);
      if (meaningful && context.navigationKey)
        interaction.navigationKeys = Math.min(10000, interaction.navigationKeys + 1);
      if (['edits', 'pastes', 'composition'].includes(context.type)) lastEditTime = now();
      if (context.type === 'edits') {
        if (lastEditAt !== undefined && interaction.timing.samples < 1000) {
          const interval = Math.min(60000, Math.max(0, clock() - lastEditAt));
          const samples = interaction.timing.samples + 1;
          const difference = interval - interaction.timing.mean;
          const mean = interaction.timing.mean + difference / samples;
          const variance = ((samples - 1) * interaction.timing.variance + difference * (interval - mean)) / samples;
          interaction.timing = { samples, mean, variance };
        }
        lastEditAt = clock();
      }
      return;
    }
    if (['searchSubmitted', 'selectionChanged', 'ratesObserved', 'checkoutEntered'].includes(action)) {
      const target = selection(
        action === 'searchSubmitted' && context.finder?.calendar
          ? { ...context, dataSource: {}, urlParams: {} }
          : context,
      );
      const kind = {
        searchSubmitted: 'search',
        selectionChanged: 'selection',
        ratesObserved: 'rates',
        checkoutEntered: 'checkout',
      }[action];
      if (kind === 'checkout') setInteractionContext(target);
      navigation[kind] = true;
      if (['search', 'rates'].includes(kind))
        journey.setContext(journeyKey({ ...target, rooms: 0 }), kind === 'search');
      if (kind === 'selection') journey.select(journeyKey(target), journeyKey({ ...target, rooms: 0 }));
      if (action === 'searchSubmitted') journey.action({ stage: 'finder', name: action });
      journey.enter(kind === 'checkout' ? 'checkout' : kind === 'search' ? 'finder' : 'rates');
      const id = `${kind}:${target.target}:${Math.floor(now() / 2000)}`;
      update({ id, at: now(), kind, ...target });
    }
  };

  const booking = (dataSource = {}, context = {}, action = 'observed') => {
    if (!dataSource.id) return;
    refresh();
    const id = `booking:${reference(dataSource.id)}`;
    const previous = records.find((record) => record.id === id);
    const cancelled = action === 'cancelled' || dataSource.state === 2;
    const target = selection({ ...context, dataSource });
    if (previous && previous.state === 'cancelled' && !cancelled) return;
    const state = cancelled ? 'cancelled' : previous?.state === 'confirmed' ? 'confirmed' : 'observed';
    if (
      previous &&
      previous.state === state &&
      previous.start === target.start &&
      previous.end === target.end &&
      previous.rooms === target.rooms &&
      (action !== 'cancelled' || previous.cancelledAt)
    )
      return;
    update({
      ...previous,
      ...target,
      id,
      kind: 'booking',
      at: now(),
      state,
      ...(action === 'cancelled' && !previous?.cancelledAt ? { cancelledAt: now() } : {}),
    });
  };

  const prepareContact = async (form = {}) => {
    if (!options.contactSignals) return;
    const revision = ++contactRevision;
    contact = undefined;
    const email = typeof form.email === 'string' ? form.email.trim().toLowerCase().slice(0, 254) : '';
    const phone = typeof form.phone === 'string' ? form.phone.replace(/\D/g, '').slice(0, 20) : '';
    if (!email || !phone || typeof crypto === 'undefined' || !crypto.subtle) return;
    try {
      const keyName = `${persistence.key}:contact`;
      let rawKey = window.localStorage.getItem(keyName);
      if (!/^[a-f0-9]{64}$/.test(rawKey || '')) {
        rawKey = [...crypto.getRandomValues(new Uint8Array(32))]
          .map((value) => value.toString(16).padStart(2, '0'))
          .join('');
        window.localStorage.setItem(keyName, rawKey);
      }
      if (!contactKey) {
        contactKey = await crypto.subtle.importKey(
          'raw',
          Uint8Array.from(rawKey.match(/.{2}/g), (value) => parseInt(value, 16)),
          { name: 'HMAC', hash: 'SHA-256' },
          false,
          ['sign'],
        );
      }
      const digest = await crypto.subtle.sign('HMAC', contactKey, new TextEncoder().encode(`${email}|${phone}`));
      if (revision === contactRevision) {
        contact = [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
      }
    } catch {
      if (revision === contactRevision) contact = undefined;
    }
  };

  const start = () => {
    if (!stopped) return;
    stopped = false;
    if (observedAt === undefined) observedAt = now();
    update({ id: `visit:${Math.floor(now() / DAY)}`, at: now(), kind: 'visit', qualified: false });
    hidden = typeof document !== 'undefined' ? document.hidden : false;
    activeSince = typeof document === 'undefined' || !document.hidden ? clock() : undefined;
    journey.visibility(!hidden);
  };

  const visibility = (nextHidden) => {
    if (hidden !== undefined && hidden !== nextHidden) visibilityChanges = Math.min(10000, visibilityChanges + 1);
    hidden = nextHidden;
    journey.visibility(!hidden && !stopped);
    if (activeSince !== undefined) activeTime += Math.max(0, clock() - activeSince);
    activeSince = hidden || stopped ? undefined : clock();
    persist();
  };

  const stop = () => {
    if (stopped) return;
    visibility(true);
    stopped = true;
  };

  return {
    scope,
    storageKey: persistence.key,
    isActive: () => !stopped,
    start,
    stop,
    record,
    booking,
    evaluate,
    check,
    request,
    observeResult,
    prepareContact,
    visibility,
    refresh,
    getAssessment: () => (assessment ? copy(assessment) : undefined),
    getSummary: () => ({
      ...copy(interaction),
      sources: { ...interaction.sources },
      modalities: [...interaction.modalities],
      timing: { ...interaction.timing },
      environment: environment(),
      records: records.length,
    }),
  };
};

export { createTrust };
